import React, { useRef, useState } from "react";
import moment from "moment";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

// The saved reports bar of Informes de Usuarios: pick one to load its filters,
// exclusions and builder settings; save the current ones under a name, or
// over the loaded one. Saved reports are shared by everyone with access.
// Confirmations are MUI dialogs, never window.confirm.

const NAME_MAX = 80;

const SAVE_ERRORS = {
  NAME_TAKEN: "Ya existe un informe guardado con ese nombre.",
  NOT_FOUND: "Ese informe ya no existe: alguien lo eliminó. Guárdelo como uno nuevo.",
  TOO_MANY: "Se alcanzó el máximo de informes guardados. Elimine alguno que ya no se use.",
  FORBIDDEN: "Su cuenta no tiene permiso para guardar informes.",
  FAILED: "No fue posible guardar el informe.",
};

const byLine = (r) => {
  const who = r.updatedBy?.name || r.createdBy?.name;
  const when = r.updatedAt ? moment(r.updatedAt).format("DD/MM/YYYY") : "";
  return [who, when].filter(Boolean).join(" · ");
};

export default function SavedUserReports({ savedReports, current, dirty, disabled, onApply, onSave, onDelete }) {
  // { mode: "new" | "rename" } while the name dialog is open.
  const [dialog, setDialog] = useState(null);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef(null);

  const openNameDialog = (mode) => {
    setName(mode === "rename" && current ? current.name : "");
    setNameError("");
    setDialog({ mode });
  };

  const submitName = async () => {
    const trimmed = name.replace(/\s+/g, " ").trim();
    if (!trimmed) return setNameError("Escriba un nombre.");
    setBusy(true);
    // A report deleted meanwhile is no longer `current`: the name then saves a new one.
    const err = await onSave({ id: dialog.mode === "rename" && current ? current.id : undefined, name: trimmed });
    setBusy(false);
    if (err) return setNameError(SAVE_ERRORS[err] || SAVE_ERRORS.FAILED);
    setDialog(null);
  };

  const saveOver = async () => {
    setBusy(true);
    await onSave({ id: current.id, name: current.name });
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    await onDelete(current.id);
    setBusy(false);
    setConfirmDelete(false);
  };

  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
        <Autocomplete
          size="small"
          sx={{ flexGrow: 1, minWidth: 0 }}
          options={savedReports}
          value={current}
          disabled={disabled}
          onChange={(_, r) => r && onApply(r)}
          getOptionLabel={(r) => r.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          disableClearable={!!current}
          renderOption={(props, r) => (
            <li {...props} key={r.id}>
              <Box>
                <Typography variant="body2">{r.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {byLine(r)}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Informes guardados"
              placeholder={savedReports.length ? "Elija uno para cargarlo" : "Aún no hay informes guardados"}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <BookmarkBorderIcon fontSize="small" color="action" sx={{ ml: 0.5 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText="Sin coincidencias"
        />
        {current && dirty && <Chip size="small" color="warning" variant="outlined" label="Con cambios sin guardar" />}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {current && (
            <Button
              size="small"
              variant="contained"
              startIcon={busy && !dialog ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={saveOver}
              disabled={disabled || busy || !dirty}
            >
              Guardar cambios
            </Button>
          )}
          {current && dirty && (
            // Picking the loaded report again in the list doesn't fire a
            // change, so going back to what was saved needs its own button.
            <Button size="small" onClick={() => onApply(current)} disabled={disabled || busy}>
              Descartar cambios
            </Button>
          )}
          <Button size="small" variant={current ? "outlined" : "contained"} onClick={() => openNameDialog("new")} disabled={disabled || busy}>
            Guardar como nuevo
          </Button>
          {current && (
            <>
              <Button size="small" onClick={() => openNameDialog("rename")} disabled={disabled || busy}>
                Renombrar
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setConfirmDelete(true)}
                disabled={disabled || busy}
              >
                Eliminar
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
        Guardan los filtros, las exclusiones y las opciones del generador, no el período. Los ven todas las personas
        con acceso a los informes.
      </Typography>

      <Dialog
        open={!!dialog}
        onClose={() => !busy && setDialog(null)}
        fullWidth
        maxWidth="xs"
        // autoFocus loses to the dialog's focus trap, which takes focus once
        // the opening transition ends: focus the name field after it.
        TransitionProps={{ onEntered: () => nameRef.current && nameRef.current.focus() }}
      >
        <DialogTitle>{dialog?.mode === "rename" ? "Renombrar informe" : "Guardar informe"}</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={nameRef}
            fullWidth
            margin="dense"
            label="Nombre"
            value={name}
            onChange={(e) => {
              setName(e.target.value.slice(0, NAME_MAX));
              setNameError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && !busy && submitName()}
            error={!!nameError}
            helperText={nameError || `${name.length}/${NAME_MAX}`}
          />
          {dialog?.mode === "rename" && dirty && (
            <DialogContentText variant="caption">
              También se guardan los cambios hechos a los filtros.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={submitName} disabled={busy}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete} onClose={() => !busy && setConfirmDelete(false)} maxWidth="xs">
        <DialogTitle>Eliminar informe guardado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            «{current?.name}» dejará de estar disponible para todas las personas con acceso a los informes. Los
            filtros que tiene en pantalla no cambian.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={remove} disabled={busy}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
